import { useState, useEffect } from 'react';
import {
  Megaphone,
  Plus,
  Edit,
  Trash2,
  Pin,
  Clock,
  Users,
  Search,
  Loader2,
  X,
  AlertTriangle,
} from 'lucide-react';
import { announcementsApi, Announcement } from '@/api/endpoints';
import { formatDistanceToNow } from 'date-fns';
import { ConfirmationModal } from '@azyntor-labs/nexus-ui';

export const AnnouncementsPage = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [deleteAnnouncementId, setDeleteAnnouncementId] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    priority: 'normal' as 'low' | 'normal' | 'high' | 'urgent',
    target_type: 'all',
    target_roles: [] as string[],
    start_at: '',
    end_at: '',
    is_pinned: false,
  });

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const response = await announcementsApi.list({ limit: 50 });
      setAnnouncements(response.data?.announcements || []);
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingAnnouncement(null);
    setFormData({
      title: '',
      content: '',
      priority: 'normal',
      target_type: 'all',
      target_roles: [],
      start_at: new Date().toISOString().slice(0, 16),
      end_at: '',
      is_pinned: false,
    });
    setShowModal(true);
  };

  const handleEdit = (announcement: Announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      content: announcement.content,
      priority: announcement.priority,
      target_type: announcement.target_type,
      target_roles: announcement.target_roles || [],
      start_at: announcement.start_at.slice(0, 16),
      end_at: announcement.end_at?.slice(0, 16) || '',
      is_pinned: announcement.is_pinned,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const data = {
        title: formData.title,
        content: formData.content,
        priority: formData.priority,
        target_type: formData.target_type,
        target_roles: formData.target_roles,
        start_at: formData.start_at ? new Date(formData.start_at).toISOString() : undefined,
        end_at: formData.end_at ? new Date(formData.end_at).toISOString() : undefined,
        is_pinned: formData.is_pinned,
      };

      if (editingAnnouncement) {
        await announcementsApi.update(editingAnnouncement.id, data);
      } else {
        await announcementsApi.create(data);
      }

      await fetchAnnouncements();
      setShowModal(false);
    } catch (error) {
      console.error('Failed to save announcement:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (id: string) => {
    setDeleteAnnouncementId(id);
    setShowConfirmDelete(true);
  };

  const confirmDeleteAnnouncement = async () => {
    if (!deleteAnnouncementId) return;
    try {
      setDeleting(deleteAnnouncementId);
      await announcementsApi.delete(deleteAnnouncementId);
      setAnnouncements((prev) => prev.filter((a) => a.id !== deleteAnnouncementId));
    } catch (error) {
      console.error('Failed to delete announcement:', error);
    } finally {
      setDeleting(null);
      setShowConfirmDelete(false);
      setDeleteAnnouncementId(null);
    }
  };

  const handleToggleActive = async (announcement: Announcement) => {
    try {
      await announcementsApi.update(announcement.id, { is_active: !announcement.is_active });
      setAnnouncements((prev) =>
        prev.map((a) => (a.id === announcement.id ? { ...a, is_active: !a.is_active } : a))
      );
    } catch (error) {
      console.error('Failed to toggle announcement:', error);
    }
  };

  const getPriorityBadge = (priority: string) => {
    const styles = {
      urgent: 'bg-red-500/10 text-red-400 border-red-500/20',
      high: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
      normal: 'bg-teal-500/10 text-teal-400 border-teal-500/20',
      low: 'bg-gray-500/10 text-gray-400 border-gray-500/20',
    };
    return styles[priority as keyof typeof styles] || styles.normal;
  };

  const filteredAnnouncements = announcements
    .filter((a) => {
      if (filterStatus === 'active') return a.is_active;
      if (filterStatus === 'inactive') return !a.is_active;
      return true;
    })
    .filter(
      (a) =>
        a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.content.toLowerCase().includes(searchQuery.toLowerCase())
    );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Announcements</h1>
          <p className="text-gray-400 mt-1 text-sm">
            Create and manage system-wide announcements
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="px-4 py-2.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-xl font-medium hover:from-teal-600 hover:to-cyan-600 shadow-lg shadow-teal-500/20 transition-all flex items-center gap-2 text-sm"
        >
          <Plus className="w-4 h-4" />
          Create Announcement
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.06] rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-xs font-medium uppercase tracking-wider">Total</p>
              <p className="text-3xl font-bold text-white mt-1">{announcements.length}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-teal-500/10 flex items-center justify-center">
              <Megaphone className="w-6 h-6 text-teal-400" />
            </div>
          </div>
        </div>

        <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.06] rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-xs font-medium uppercase tracking-wider">Active</p>
              <p className="text-3xl font-bold text-white mt-1">{announcements.filter((a) => a.is_active).length}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
              <Users className="w-6 h-6 text-emerald-400" />
            </div>
          </div>
        </div>

        <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.06] rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-xs font-medium uppercase tracking-wider">Pinned</p>
              <p className="text-3xl font-bold text-white mt-1">{announcements.filter((a) => a.is_pinned).length}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
              <Pin className="w-6 h-6 text-amber-400" />
            </div>
          </div>
        </div>

        <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.06] rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-xs font-medium uppercase tracking-wider">Urgent</p>
              <p className="text-3xl font-bold text-white mt-1">{announcements.filter((a) => a.priority === 'urgent').length}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.06] rounded-xl p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <input
              type="text"
              placeholder="Search announcements..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-[#0d0d12] border border-white/[0.06] rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500/50 text-sm"
            />
          </div>

          <div className="flex gap-1 p-1 bg-[#0d0d12] rounded-xl border border-white/[0.06]">
            {['all', 'active', 'inactive'].map((status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status as any)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all capitalize ${
                  filterStatus === status
                    ? 'bg-teal-500/20 text-teal-400'
                    : 'text-gray-400 hover:text-white hover:bg-white/[0.04]'
                }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Announcements List */}
      <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.06] rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
          </div>
        ) : filteredAnnouncements.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 rounded-2xl bg-white/[0.04] flex items-center justify-center mx-auto mb-4">
              <Megaphone className="w-8 h-8 text-gray-500" />
            </div>
            <p className="text-white font-semibold text-lg mb-2">No announcements</p>
            <p className="text-gray-400 text-sm">Create your first announcement to get started</p>
          </div>
        ) : (
          <div className="divide-y divide-white/[0.04]">
            {filteredAnnouncements.map((announcement) => (
              <div
                key={announcement.id}
                className={`px-6 py-4 hover:bg-white/[0.02] transition-colors ${
                  !announcement.is_active ? 'opacity-60' : ''
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    announcement.is_pinned ? 'bg-amber-500/10' : 'bg-purple-500/10'
                  }`}>
                    {announcement.is_pinned ? (
                      <Pin className="w-5 h-5 text-amber-400" />
                    ) : (
                      <Megaphone className="w-5 h-5 text-purple-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-white">{announcement.title}</h4>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border ${getPriorityBadge(announcement.priority)}`}>
                            {announcement.priority}
                          </span>
                          {!announcement.is_active && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-gray-500/10 text-gray-400 border border-gray-500/20">
                              Inactive
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-400 mt-1 line-clamp-2">{announcement.content}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => handleToggleActive(announcement)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                            announcement.is_active
                              ? 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'
                              : 'bg-gray-500/10 text-gray-400 hover:bg-gray-500/20'
                          }`}
                        >
                          {announcement.is_active ? 'Active' : 'Inactive'}
                        </button>
                        <button
                          onClick={() => handleEdit(announcement)}
                          className="p-2 rounded-lg text-gray-400 hover:text-teal-400 hover:bg-teal-500/10 transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(announcement.id)}
                          disabled={deleting === announcement.id}
                          className="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                        >
                          {deleting === announcement.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {announcement.target_type === 'all' ? 'Everyone' : `${announcement.target_type}: ${announcement.target_roles?.join(', ') || 'All'}`}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDistanceToNow(new Date(announcement.created_at), { addSuffix: true })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={showConfirmDelete}
        onClose={() => { setShowConfirmDelete(false); setDeleteAnnouncementId(null); }}
        onConfirm={confirmDeleteAnnouncement}
        title="Delete Announcement?"
        message="Are you sure you want to delete this announcement?"
        variant="danger"
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#12121a] border border-white/[0.08] rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/[0.06] flex items-center justify-between sticky top-0 bg-[#12121a] z-10">
              <div>
                <h2 className="text-xl font-bold text-white">
                  {editingAnnouncement ? 'Edit Announcement' : 'Create Announcement'}
                </h2>
                <p className="text-gray-400 text-sm mt-1">
                  {editingAnnouncement ? 'Update the announcement details' : 'Create a new announcement for users'}
                </p>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 rounded-lg bg-white/[0.04] text-gray-400 hover:bg-white/[0.08] hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Enter announcement title"
                  className="w-full px-4 py-2.5 bg-[#0d0d12] border border-white/[0.06] rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500/50 text-sm"
                />
              </div>

              {/* Content */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Content</label>
                <textarea
                  rows={4}
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Enter announcement content"
                  className="w-full px-4 py-2.5 bg-[#0d0d12] border border-white/[0.06] rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500/50 text-sm resize-none"
                />
              </div>

              {/* Priority & Target */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Priority</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
                    className="w-full px-4 py-2.5 bg-[#0d0d12] border border-white/[0.06] rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-teal-500/50 text-sm"
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Target Audience</label>
                  <select
                    value={formData.target_type}
                    onChange={(e) => setFormData({ ...formData, target_type: e.target.value })}
                    className="w-full px-4 py-2.5 bg-[#0d0d12] border border-white/[0.06] rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-teal-500/50 text-sm"
                  >
                    <option value="all">Everyone</option>
                    <option value="role">By Role</option>
                  </select>
                </div>
              </div>

              {/* Role Selection */}
              {formData.target_type === 'role' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Target Roles</label>
                  <div className="flex flex-wrap gap-2">
                    {['student', 'teacher', 'instructor', 'admin', 'proctor'].map((role) => (
                      <label
                        key={role}
                        className={`px-3 py-1.5 rounded-lg text-sm cursor-pointer transition-colors ${
                          formData.target_roles.includes(role)
                            ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30'
                            : 'bg-white/[0.04] text-gray-400 border border-white/[0.06] hover:bg-white/[0.08]'
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="hidden"
                          checked={formData.target_roles.includes(role)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({ ...formData, target_roles: [...formData.target_roles, role] });
                            } else {
                              setFormData({ ...formData, target_roles: formData.target_roles.filter((r) => r !== role) });
                            }
                          }}
                        />
                        <span className="capitalize">{role}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Start Date</label>
                  <input
                    type="datetime-local"
                    value={formData.start_at}
                    onChange={(e) => setFormData({ ...formData, start_at: e.target.value })}
                    className="w-full px-4 py-2.5 bg-[#0d0d12] border border-white/[0.06] rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-teal-500/50 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">End Date (Optional)</label>
                  <input
                    type="datetime-local"
                    value={formData.end_at}
                    onChange={(e) => setFormData({ ...formData, end_at: e.target.value })}
                    className="w-full px-4 py-2.5 bg-[#0d0d12] border border-white/[0.06] rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-teal-500/50 text-sm"
                  />
                </div>
              </div>

              {/* Pin */}
              <label className="flex items-center gap-3 p-4 bg-[#0d0d12] rounded-xl border border-white/[0.06] cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_pinned}
                  onChange={(e) => setFormData({ ...formData, is_pinned: e.target.checked })}
                  className="w-5 h-5 rounded bg-[#0d0d12] border-white/[0.2] text-teal-500 focus:ring-teal-500 focus:ring-offset-0"
                />
                <div>
                  <p className="text-white font-medium">Pin this announcement</p>
                  <p className="text-gray-500 text-xs">Pinned announcements appear at the top of the list</p>
                </div>
              </label>
            </div>

            <div className="p-6 border-t border-white/[0.06] flex gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 px-4 py-2.5 bg-white/[0.04] border border-white/[0.06] text-gray-300 rounded-xl hover:bg-white/[0.08] font-medium text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !formData.title || !formData.content}
                className="flex-1 px-4 py-2.5 bg-teal-500 text-white rounded-xl hover:bg-teal-600 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editingAnnouncement ? 'Update' : 'Create'} Announcement
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnouncementsPage;
